import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import {
  TicketReservationContextUiModel,
  TicketUiModel
} from '../../../../shared/ui-models/ticket.ui-model';
import { getTicketReservationContext } from '../../../../shared/services/data/ticket.data';
import { APP_ROUTES } from '../../../../shared/config/routes-config';
import { RouteConfig } from '../../../../shared/interfaces/routes-config.interface';
import { titleCase } from '../../../shared/ticket-visuals';

const CARD = 'rounded-2xl border border-gray-200 bg-white p-4 shadow-md';
const ROW = 'flex justify-between gap-3 py-1 text-sm';
const KEY = 'text-neutral-300';
const VALUE = 'text-right text-neutral-500';

const reservationPath = (id: string) =>
  (APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig).EDIT_NEW?.FULL_PATH.replace(':id', id);

const unitTypePath = (id: string) =>
  (APP_ROUTES.UNIT_TYPE_MODULE.CHILDREN as RouteConfig).EDIT?.FULL_PATH.replace(':id', id);

/**
 * What the agent needs beside the thread: how CX said to handle this category, and
 * the reservation, guest and unit the ticket is about. The reservation is read with
 * the agent's own access. Without it the panel shows only the reference.
 */
export const TicketContext: React.FC<{ ticket: TicketUiModel }> = ({ ticket }) => {
  const [reservation, setReservation] = useState<TicketReservationContextUiModel | undefined>();
  const [reservationHidden, setReservationHidden] = useState(false);

  useEffect(() => {
    setReservation(undefined);
    setReservationHidden(false);
    if (!ticket.reservationRef) return;
    getTicketReservationContext(ticket.reservationRef).then((result) => {
      if (isErrorInfo(result)) setReservationHidden(true);
      else setReservation(result);
    });
  }, [ticket.reservationRef]);

  const resPath = ticket.reservationRef ? reservationPath(ticket.reservationRef) : undefined;
  const unitPath = ticket.unitRef ? unitTypePath(ticket.unitRef) : undefined;

  return (
    <div className="flex flex-col gap-4">
      {ticket.handling ? (
        <div className={CARD}>
          <h3 className="mb-2 font-gilroy-bold text-neutral-500">How to handle</h3>
          <p className="whitespace-pre-wrap text-sm text-neutral-400">{ticket.handling}</p>
        </div>
      ) : null}

      <div className={CARD}>
        <h3 className="mb-2 font-gilroy-bold text-neutral-500">Routing</h3>
        <div className={ROW}>
          <span className={KEY}>Category</span>
          <span className={VALUE}>{ticket.categoryLabel ?? '—'}</span>
        </div>
        <div className={ROW}>
          <span className={KEY}>Now with</span>
          <span className={VALUE}>{ticket.team}</span>
        </div>
        <div className={ROW}>
          <span className={KEY}>Next</span>
          <span className={VALUE}>
            {ticket.nextOwner
              ? `${ticket.nextOwner.teams.join(' / ')}${
                  ticket.nextOwner.when ? ` (${ticket.nextOwner.when})` : ''
                }`
              : 'End of the chain'}
          </span>
        </div>
      </div>

      {ticket.reservationRef ? (
        <div className={CARD}>
          <h3 className="mb-2 flex items-center justify-between font-gilroy-bold text-neutral-500">
            Reservation
            {resPath ? (
              <Link to={resPath} className="text-xs font-gilroy-medium text-primary-700">
                Open
              </Link>
            ) : null}
          </h3>
          {reservation ? (
            <>
              <div className={ROW}>
                <span className={KEY}>Guest</span>
                <span className={VALUE}>{reservation.guestName}</span>
              </div>
              {reservation.guestPhone ? (
                <div className={ROW}>
                  <span className={KEY}>Phone</span>
                  <span className={VALUE}>{reservation.guestPhone}</span>
                </div>
              ) : null}
              {reservation.guestEmail ? (
                <div className={ROW}>
                  <span className={KEY}>Email</span>
                  <span className={`${VALUE} break-all`}>{reservation.guestEmail}</span>
                </div>
              ) : null}
              <div className={ROW}>
                <span className={KEY}>Stay</span>
                <span className={VALUE}>
                  {reservation.checkIn.toLocaleDateString()} →{' '}
                  {reservation.checkOut.toLocaleDateString()}
                </span>
              </div>
              <div className={ROW}>
                <span className={KEY}>Unit</span>
                <span className={VALUE}>
                  {reservation.propertyName} · {reservation.unitName}
                </span>
              </div>
              <div className={ROW}>
                <span className={KEY}>Status</span>
                <span className={VALUE}>{titleCase(reservation.status)}</span>
              </div>
              {reservation.channel ? (
                <div className={ROW}>
                  <span className={KEY}>Channel</span>
                  <span className={VALUE}>{reservation.channel}</span>
                </div>
              ) : null}
            </>
          ) : reservationHidden ? (
            <p className="text-sm text-neutral-300">
              {ticket.reservationRef} (you cannot open this reservation)
            </p>
          ) : (
            <p className="text-sm text-neutral-300">Loading…</p>
          )}
        </div>
      ) : null}

      {ticket.unitRef ? (
        <div className={CARD}>
          <h3 className="flex items-center justify-between font-gilroy-bold text-neutral-500">
            Unit type
            {unitPath ? (
              <Link to={unitPath} className="text-xs font-gilroy-medium text-primary-700">
                Open
              </Link>
            ) : null}
          </h3>
        </div>
      ) : null}
    </div>
  );
};
